import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  Upload,
} from 'antd';
import type { UploadProps } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import {
  MediaAssetDto,
  useDeleteMedia,
  useMediaAssets,
  useUpdateMedia,
  uploadMediaAsset,
} from '@shared/api/hooks/useMediaAssets';

interface UploadFormValues {
  label: string;
  position: number;
  ctaUrl?: string;
  file: { fileList: Array<{ originFileObj: File }> };
}

interface EditFormValues {
  label: string;
  position: number;
  ctaUrl?: string;
}

export function HeroBannersPage(): React.ReactElement {
  const { data: rows = [], isLoading, refetch } = useMediaAssets({ category: 'HERO_BANNER' });
  const deleteMutation = useDeleteMedia();
  const updateMutation = useUpdateMedia();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm] = Form.useForm<UploadFormValues>();
  const [uploading, setUploading] = useState(false);

  const [editId, setEditId] = useState<string | null>(null);
  const [editForm] = Form.useForm<EditFormValues>();
  const editRow = useMemo(() => rows.find((r) => r.id === editId) ?? null, [rows, editId]);

  const openEdit = (row: MediaAssetDto): void => {
    setEditId(row.id);
    editForm.setFieldsValue({
      label: row.label,
      position: row.position,
      ctaUrl: row.ctaUrl ?? undefined,
    });
  };

  const onUpload = async (): Promise<void> => {
    const v = await uploadForm.validateFields();
    const file = v.file?.fileList?.[0]?.originFileObj;
    if (!file) {
      message.error('Carica un file');
      return;
    }
    setUploading(true);
    try {
      const asset = await uploadMediaAsset('HERO_BANNER', v.label, file);
      // After upload set position/ctaUrl if user filled them
      if (v.position != null || v.ctaUrl) {
        await updateMutation.mutateAsync({
          id: asset.id,
          input: { position: v.position, ctaUrl: v.ctaUrl ?? null },
        });
      }
      message.success('Banner caricato');
      setUploadOpen(false);
      uploadForm.resetFields();
      refetch();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } }; message?: string };
      const msg = err.response?.data?.message ?? err.message ?? 'Errore upload';
      message.error(Array.isArray(msg) ? msg.join('; ') : msg);
    } finally {
      setUploading(false);
    }
  };

  const onEdit = async (): Promise<void> => {
    if (!editId) return;
    const v = await editForm.validateFields();
    try {
      await updateMutation.mutateAsync({
        id: editId,
        input: { label: v.label, position: v.position, ctaUrl: v.ctaUrl ?? null },
      });
      message.success('Banner aggiornato');
      setEditId(null);
    } catch (e) {
      message.error((e as Error).message ?? 'Errore');
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Banner eliminato');
    } catch (e) {
      message.error((e as Error).message ?? 'Errore');
    }
  };

  const uploadProps: UploadProps = {
    multiple: false,
    maxCount: 1,
    accept: 'image/png,image/jpeg,image/webp',
    beforeUpload: () => false, // prevent auto-upload; we drive it manually
  };

  return (
    <Card
      title="Banner Home App (fallback no-match)"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setUploadOpen(true)}>
          Carica banner
        </Button>
      }
    >
      <Typography.Paragraph type="secondary">
        Slider mostrato in cima alla Home dell&apos;app quando non ci sono partite in programma o LIVE.
        Ordine: <strong>position</strong> crescente (0 = primo). Tap apre l&apos;URL CTA in browser.
      </Typography.Paragraph>
      <Table
        rowKey="id"
        loading={isLoading}
        pagination={false}
        dataSource={rows}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Anteprima',
            key: 'preview',
            width: 130,
            render: (_v, r: MediaAssetDto) => (
              <img
                src={r.url}
                alt={r.label}
                style={{ width: 120, height: 60, objectFit: 'cover', borderRadius: 6 }}
              />
            ),
          },
          { title: 'Label', dataIndex: 'label' },
          { title: 'Position', dataIndex: 'position', width: 90 },
          {
            title: 'CTA',
            dataIndex: 'ctaUrl',
            render: (v: string | null) =>
              v ? (
                <a href={v} target="_blank" rel="noreferrer">
                  {v}
                </a>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Azioni',
            key: 'actions',
            width: 140,
            render: (_v, r: MediaAssetDto) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                  Modifica
                </Button>
                <Popconfirm
                  title="Elimina banner?"
                  okText="Elimina"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="Carica banner"
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        onOk={onUpload}
        confirmLoading={uploading}
        okText="Carica"
        destroyOnClose
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name="file"
            label="Immagine (16:9 o 3:1 consigliato)"
            rules={[{ required: true, message: 'Carica un file' }]}
            valuePropName="file"
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Seleziona file</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="label" label="Label" rules={[{ required: true }]}>
            <Input placeholder="es. Apertura abbonamenti" />
          </Form.Item>
          <Form.Item name="position" label="Position (ordine)" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ctaUrl" label="CTA URL (opzionale)">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Modifica banner${editRow ? ` — ${editRow.label}` : ''}`}
        open={editId !== null}
        onCancel={() => setEditId(null)}
        onOk={onEdit}
        okText="Salva"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="label" label="Label" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="position" label="Position (ordine)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ctaUrl" label="CTA URL">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
